import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Button, Card, Container, Spinner, Table, Modal, Row, Col
} from 'react-bootstrap';
import { FaFileExcel, FaChartBar, FaSyncAlt } from 'react-icons/fa';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS, BarElement, CategoryScale, LinearScale,
  PointElement, Title, Tooltip, Legend, Filler
} from 'chart.js';
import { motion, AnimatePresence } from 'framer-motion';
import * as XLSX from 'xlsx';

ChartJS.register(
  BarElement, CategoryScale, LinearScale,
  PointElement, Title, Tooltip, Legend, Filler
);

const COLORS = [
  'rgba(54, 162, 235, 0.7)',
  'rgba(255, 99, 132, 0.7)',
  'rgba(75, 192, 192, 0.7)',
  'rgba(255, 206, 86, 0.7)',
  'rgba(153, 102, 255, 0.7)',
  'rgba(255, 159, 64, 0.7)'
];

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.5, ease: "easeOut" }
  },
  exit: { opacity: 0, y: -20 }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const SPECIAL_BOOKKEEPING_USER_IDS = ['691c4908d1fab55e7f2c2ef5', '690c8991d6e0a0f153191140'];
const BOOKKEEPING_STEPS = [
  {
    title: 'Step 1',
    description: 'Upload the Bank / Credit Card & Other Documents'
  },
  {
    title: 'Step 2',
    description: 'Nature of Transaction Required'
  },
  {
    title: 'Step 3',
    description: 'Financial Report'
  }
];

const UserBookkeeping = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [bookkeepingData, setBookkeepingData] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [userData, setUserData] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadMessage, setUploadMessage] = useState('');
  const [uploadError, setUploadError] = useState('');
  const [activeStepIndex, setActiveStepIndex] = useState(null);

  useEffect(() => {
    fetchBookkeepingData();
  }, []);

  const fetchBookkeepingData = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }

    try {
      setIsRefreshing(true);
      setError('');

      const userResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/user`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!userResponse.ok) throw new Error('Failed to fetch user data');
      const userDataResponse = await userResponse.json();
      setUserData(userDataResponse.user);

      const bookkeepingResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/bookkeeping/${userDataResponse.user._id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!bookkeepingResponse.ok) {
        if (bookkeepingResponse.status === 404) {
          setBookkeepingData(null);
          return;
        }
        throw new Error('Failed to fetch bookkeeping data');
      }

      const data = await bookkeepingResponse.json();
      setBookkeepingData(data);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes && bytes !== 0) return '0 B';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const isUploadStep = (index) => [0, 2].includes(index);

  const handleStepClick = (stepIndex) => {
    if (isUploadStep(stepIndex) && showSpecialSteps) {
      setActiveStepIndex(stepIndex);
      setShowUploadModal(true);
      setUploadMessage('');
      setUploadError('');
    }
  };

  const handleCloseUploadModal = () => {
    if (uploading) return;
    setShowUploadModal(false);
    setActiveStepIndex(null);
    setSelectedFiles([]);
    setUploadMessage('');
    setUploadError('');
  };

  const handleFileSelection = (event) => {
    setSelectedFiles(Array.from(event.target.files || []));
    setUploadMessage('');
    setUploadError('');
  };

  const handleUploadSubmit = async () => {
    if (!selectedFiles.length) {
      setUploadError('Please select at least one file.');
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }

    if (!userData?._id) {
      setUploadError('User information is missing. Please refresh and try again.');
      return;
    }

    try {
      setUploading(true);
      setUploadError('');
      setUploadMessage('');

      const formData = new FormData();
      selectedFiles.forEach(file => formData.append('files', file));
      if (activeStepIndex !== null) {
        formData.append('step', BOOKKEEPING_STEPS[activeStepIndex].title);
      }

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/bookkeeping/${userData._id}/files`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.message || 'Failed to upload files.');
      }

      const data = await response.json();
      setUploadMessage(data.message || 'Files uploaded successfully.');
      setSelectedFiles([]);
      await fetchBookkeepingData();
    } catch (uploadErr) {
      setUploadError(uploadErr.message || 'Failed to upload files.');
    } finally {
      setUploading(false);
    }
  };

  const exportToExcel = () => {
    if (!bookkeepingData) return;
    
    const worksheet = XLSX.utils.json_to_sheet(
      bookkeepingData.rows.filter(row => !row.include)
    );
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Bookkeeping Data");
    XLSX.writeFile(workbook, `${bookkeepingData.title || 'bookkeeping_data'}.xlsx`);
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { 
        position: 'top',
        labels: {
          font: {
            family: 'Arial',
            size: 12,
            weight: 'bold'
          },
          padding: 20,
          usePointStyle: true,
        }
      },
      title: {
        display: true,
        text: bookkeepingData?.title || 'Bookkeeping Data',
        font: { 
          size: 18,
          family: 'Arial',
          weight: 'bold'
        },
        padding: {
          top: 10,
          bottom: 20
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0,0,0,0.8)',
        titleFont: {
          size: 14,
          weight: 'bold'
        },
        bodyFont: {
          size: 12
        },
        padding: 10,
        usePointStyle: true,
        callbacks: {
          label: function(context) {
            return ` $${context.parsed.y.toLocaleString()}`;
          }
        }
      }
    },
    animation: {
      duration: 1500,
      easing: 'easeInOutQuart',
    },
    elements: {
      bar: {
        borderRadius: 6,
        borderSkipped: 'bottom',
      }
    },
    scales: {
      x: {
        grid: {
          display: false,
          drawBorder: false
        },
        ticks: {
          font: {
            weight: 'bold'
          }
        }
      },
      y: {
        beginAtZero: false,
        grid: {
          color: 'rgba(0,0,0,0.05)',
          drawBorder: false
        },
        ticks: {
          callback: value => '$' + value.toLocaleString(),
          font: {
            weight: 'bold'
          }
        },
      },
    },
    layout: {
      padding: {
        left: 20,
        right: 20,
        top: 20,
        bottom: 20
      }
    }
  };

  if (loading) {
    return (
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="d-flex justify-content-center align-items-center vh-100"
      >
        <div className="text-center">
          <Spinner animation="grow" variant="primary" size="lg" />
          <p className="mt-3 text-muted">Loading your financial data...</p>
        </div>
      </motion.div>
    );
  }

  const showSpecialSteps = userData && SPECIAL_BOOKKEEPING_USER_IDS.includes(userData._id);

  return (
    <Container fluid className="py-4 px-3 px-md-4">
      {/* Bundle Activation Message */}
      {userData?.isBundlePurchased && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-4"
        >
          <Alert 
            variant="success" 
            className="d-flex align-items-center shadow-sm"
            style={{
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              padding: '15px 20px',
              fontSize: '16px',
              fontWeight: 'bold'
            }}
          >
            <span style={{ fontSize: '24px', marginRight: '10px' }}>✅</span>
            <span>1099 Form is Activated - 4 Nos</span>
          </Alert>
        </motion.div>
      )}

      {showSpecialSteps && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-4"
        >
          <Card className="shadow border-0">
            <Card.Header className="bg-white border-0 pb-0">
              <div className="d-flex align-items-center gap-3">
                <span className="badge bg-primary rounded-pill px-3 py-2">Bookkeeping Process</span>
                <p className="mb-0 text-muted small">
                  Complete the steps in sequence to keep your bookkeeping bundle on track.
                </p>
              </div>
            </Card.Header>
            <Card.Body className="pt-3">
              <Row className="g-3">
                {BOOKKEEPING_STEPS.map((step, index) => (
                  <Col xs={12} md={4} key={step.title}>
                    <div
                      className="h-100 p-3 rounded-3"
                      style={{
                        border: '1px solid rgba(58,123,213,0.15)',
                        background: 'linear-gradient(135deg, rgba(58,123,213,0.08), rgba(0,210,255,0.08))',
                        cursor: isUploadStep(index) ? 'pointer' : 'default'
                      }}
                      onClick={() => handleStepClick(index)}
                    >
                      <div className="d-flex align-items-center mb-2">
                        <div
                          className="rounded-circle d-flex align-items-center justify-content-center me-2"
                          style={{
                            width: '36px',
                            height: '36px',
                            backgroundColor: 'rgba(58,123,213,0.2)',
                            color: '#3a7bd5',
                            fontWeight: 'bold'
                          }}
                        >
                          {index + 1}
                        </div>
                        <h6 className="mb-0 text-primary">{step.title}</h6>
                      </div>
                      <p className="mb-0 text-dark fw-semibold" style={{ fontSize: '0.95rem' }}>
                        {step.description}
                      </p>
                      {isUploadStep(index) && (
                        <small className="text-muted d-block mt-2">
                          {index === 0
                            ? ''
                            : ''}
                        </small>
                      )}
                    </div>
                  </Col>
                ))}
              </Row>
            </Card.Body>
          </Card>
        </motion.div>
      )}

      <AnimatePresence mode="wait">
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="mb-4"
          >
            <Alert 
              variant="danger" 
              className="d-flex flex-column flex-md-row justify-content-between align-items-center shadow-sm"
            >
              <div className="mb-2 mb-md-0">
                <strong>Error:</strong> {error}
              </div>
              <Button 
                variant="outline-danger" 
                size="sm" 
                onClick={fetchBookkeepingData}
                disabled={isRefreshing}
              >
                {isRefreshing ? 'Refreshing...' : 'Try Again'}
              </Button>
            </Alert>
          </motion.div>
        )}

        <motion.div
          variants={staggerContainer}
          initial="hidden"
          animate="visible"
          className="d-flex flex-column gap-4"
        >
          {bookkeepingData ? (
            <>
              {/* Additional Data Table */}
              <motion.div variants={fadeIn}>
                <Card className="shadow-lg border-0">
                  <Card.Header className="bg-gradient-primary text-white d-flex flex-column flex-md-row justify-content-between align-items-center">
                    <div className="d-flex align-items-center mb-2 mb-md-0">
                      <FaFileExcel className="me-2" size={20} />
                      <h5 className="mb-0">{bookkeepingData.additionalTitle || 'Financial Records'}</h5>
                    </div>
                    <div className="d-flex">
                      <Button 
                        variant="light" 
                        size="sm" 
                        className="me-2"
                        onClick={exportToExcel}
                      >
                        <span className="d-none d-md-inline">Export Excel</span>
                        <span className="d-inline d-md-none"><FaFileExcel /></span>
                      </Button>
                      <Button 
                        variant="light" 
                        size="sm" 
                        onClick={fetchBookkeepingData}
                        disabled={isRefreshing}
                      >
                        <FaSyncAlt className={isRefreshing ? "spin" : ""} />
                      </Button>
                    </div>
                  </Card.Header>
                  <Card.Body className="p-0">
                    <div className="table-responsive">
                      <Table hover className="mb-0">
                        <thead className="bg-light">
                          <tr>
                            {bookkeepingData.headers.map((header, index) => (
                              <th key={index} className="py-3 px-2 px-md-4 text-uppercase text-muted">
                                {header}
                              </th>
                            ))}
                          </tr>
                        </thead>  
                        <tbody>
                          {bookkeepingData.rows.filter(row => !row.include).map((row, rowIndex) => (
                            <motion.tr 
                              key={rowIndex}
                              initial={{ opacity: 0, x: -20 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: rowIndex * 0.05 }}
                              className={rowIndex % 2 === 0 ? 'bg-white' : 'bg-light'}
                            >
                              {bookkeepingData.headers.map((header, colIndex) => (
                                <td key={colIndex} className="py-3 px-2 px-md-4">
                                  {typeof row[header] === 'number' ? 
                                    `$${row[header].toLocaleString()}` : 
                                    row[header]}
                                </td>
                              ))}
                            </motion.tr>
                          ))}
                          {!bookkeepingData.rows.filter(row => !row.include).length && (
                            <tr>
                              <td colSpan={bookkeepingData.headers.length} className="text-center text-muted py-5">
                                No additional data available
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </Table>
                    </div>
                  </Card.Body>
                </Card>
              </motion.div>

              {/* Chart Section */}
              {bookkeepingData.chartData && (
                <motion.div variants={fadeIn}>
                  <Card className="shadow-lg border-0">
                    <Card.Header className="bg-gradient-primary text-white d-flex flex-column flex-md-row justify-content-between align-items-center">
                      <div className="d-flex align-items-center mb-2 mb-md-0">
                        <FaChartBar className="me-2" size={20} />
                        <h5 className="mb-0">{bookkeepingData.title || 'Financial Overview'}</h5>
                      </div>
                      <div>
                        <Button 
                          variant="light" 
                          size="sm" 
                          onClick={fetchBookkeepingData}
                          disabled={isRefreshing}
                        >
                          <FaSyncAlt className={isRefreshing ? "spin" : ""} />
                        </Button>
                      </div>
                    </Card.Header>
                    <Card.Body style={{ minHeight: '300px', height: '50vh' }} className="p-2 p-md-4">
                      <Bar 
                        data={bookkeepingData.chartData} 
                        options={chartOptions} 
                        className="w-100 h-100"
                      />
                    </Card.Body>
                  </Card>
                </motion.div>
              )}
            </>
          ) : (
            <motion.div variants={fadeIn}>
              <Card className="text-center py-5 border-0 shadow-sm">
                <Card.Body>
                  <motion.div
                    animate={{ 
                      scale: [1, 1.05, 1],
                      rotate: [0, 5, -5, 0]
                    }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                  >
                    <FaFileExcel size={64} className="text-primary mb-3" />
                  </motion.div>
                  <h4 className="text-dark mb-3">No bookkeeping data available</h4>
                  <p className="text-muted mb-4">
                    Please contact your administrator to set up your financial records
                  </p>
                  <Button 
                    variant="primary" 
                    onClick={fetchBookkeepingData}
                    disabled={isRefreshing}
                  >
                    {isRefreshing ? 'Checking...' : 'Check Again'}
                  </Button>
                </Card.Body>
              </Card>
            </motion.div>
          )}
        </motion.div>
      </AnimatePresence>

      <style jsx global>{`
        .bg-gradient-primary {
          background: linear-gradient(135deg, #3a7bd5 0%, #00d2ff 100%);
        }
        .spin {
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .table-responsive {
          border-radius: 0.5rem;
          overflow: hidden;
        }
        thead {
          backdrop-filter: blur(10px);
        }
        
        /* Responsive adjustments */
        @media (max-width: 768px) {
          .table-responsive {
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
          }
          .chart-container {
            min-height: 300px !important;
          }
        }
        
        @media (max-width: 576px) {
          .card-header h5 {
            font-size: 1.1rem;
          }
          .table th, .table td {
            padding: 0.5rem;
            font-size: 0.85rem;
          }
        }
      `}</style>
      <Modal show={showUploadModal} onHide={handleCloseUploadModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            Upload Bookkeeping Documents {activeStepIndex !== null ? `(${BOOKKEEPING_STEPS[activeStepIndex].title})` : ''}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted">
            {activeStepIndex === 2
              ? 'Upload your completed Financial Report documents (Step 3).'
              : 'Please upload your Bank / Credit Card statements and other supporting documents (Step 1).'}
          </p>
          <div className="mb-3">
            <input
              type="file"
              multiple
              className="form-control"
              onChange={handleFileSelection}
              disabled={uploading}
            />
          </div>
          {selectedFiles.length > 0 && (
            <div className="mb-3">
              <p className="fw-semibold mb-2">Selected Files</p>
              <ul className="list-group">
                {selectedFiles.map((file, idx) => (
                  <li key={`${file.name}-${idx}`} className="list-group-item d-flex justify-content-between align-items-center">
                    <span className="text-truncate" style={{ maxWidth: '65%' }}>{file.name}</span>
                    <small className="text-muted">{formatFileSize(file.size)}</small>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {uploadError && (
            <Alert variant="danger" className="mb-2">{uploadError}</Alert>
          )}
          {uploadMessage && (
            <Alert variant="success" className="mb-0">{uploadMessage}</Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseUploadModal} disabled={uploading}>
            Close
          </Button>
          <Button
            variant="primary"
            onClick={handleUploadSubmit}
            disabled={uploading || !selectedFiles.length}
          >
            {uploading ? 'Uploading...' : 'Upload Files'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default UserBookkeeping;